import { useCallback, useEffect, useState } from 'react'
import {
  awardShopBadge,
  BADGE_EARLY_SHOPS_PROGRAM_FOUNDING,
  listShopBadges,
  revokeShopBadge,
  type ShopBadgeRead,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission } from '../../hooks/usePermission'

interface BadgesPanelProps {
  shopId: string
}

export function BadgesPanel({ shopId }: BadgesPanelProps) {
  const [badges, setBadges] = useState<ShopBadgeRead[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [revokeReason, setRevokeReason] = useState('')
  const [revokingBadgeType, setRevokingBadgeType] = useState<string | null>(null)
  const [showAwardForm, setShowAwardForm] = useState(false)
  const [customBadgeType, setCustomBadgeType] = useState('')

  const canEdit = usePermission('shops', 'edit')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const res = await listShopBadges(shopId, false)
    if (res.ok && res.data) setBadges(res.data)
    else setError(res.error?.message || 'Failed to load badges')
    setLoading(false)
  }, [shopId])

  useEffect(() => {
    load()
  }, [load])

  const handleAwardFounding = async () => {
    setSubmitting(true)
    setError(null)
    const res = await awardShopBadge(shopId, BADGE_EARLY_SHOPS_PROGRAM_FOUNDING, {})
    setSubmitting(false)
    if (!res.ok) {
      setError(res.error?.message || 'Failed to award founding badge.')
      return
    }
    await load()
  }

  const handleAwardCustom = async () => {
    if (!customBadgeType.trim()) return
    setSubmitting(true)
    setError(null)
    const res = await awardShopBadge(shopId, customBadgeType.trim(), {})
    setSubmitting(false)
    if (!res.ok) {
      setError(res.error?.message || 'Failed to award badge.')
      return
    }
    setCustomBadgeType('')
    setShowAwardForm(false)
    await load()
  }

  const handleRevoke = async (badgeType: string) => {
    setSubmitting(true)
    setError(null)
    const res = await revokeShopBadge(shopId, badgeType, revokeReason.trim() || undefined)
    setSubmitting(false)
    if (!res.ok) {
      setError(res.error?.message || 'Failed to revoke badge.')
      return
    }
    setRevokeReason('')
    setRevokingBadgeType(null)
    await load()
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  const hasFoundingBadge = badges.some(
    (b) => b.badge_type === BADGE_EARLY_SHOPS_PROGRAM_FOUNDING,
  )

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-semibold text-gray-900 dark:text-gray-100">Active badges</h4>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Founding badges persist after the 1-year grandfather period and are revocable
          only via admin moderation.
        </p>
      </div>

      {error && (
        <div className="rounded-xl bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {badges.length === 0 ? (
        <div className="rounded-xl bg-gray-50 dark:bg-[#2a1f35] p-4 text-sm text-gray-600 dark:text-gray-300">
          No active badges.
        </div>
      ) : (
        <div className="space-y-2">
          {badges.map((badge) => (
            <div
              key={badge.id}
              className="rounded-xl border border-gray-200 dark:border-[#3d2c4f] bg-white dark:bg-[#2a1f35] p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-sm text-gray-900 dark:text-gray-100">
                    {badge.badge_type}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Earned {new Date(badge.earned_at).toLocaleString()}
                  </div>
                </div>
                {canEdit && revokingBadgeType !== badge.badge_type && (
                  <CustomButton
                    onClick={() => setRevokingBadgeType(badge.badge_type)}
                    variant="danger"
                    size="sm"
                  >
                    Revoke
                  </CustomButton>
                )}
              </div>
              {revokingBadgeType === badge.badge_type && (
                <div className="mt-3 space-y-2">
                  <CustomTextInput
                    label="Revocation reason (optional)"
                    value={revokeReason}
                    onChange={(e) => setRevokeReason(e.target.value)}
                    placeholder="Audit context"
                  />
                  <div className="flex gap-2">
                    <CustomButton
                      onClick={() => handleRevoke(badge.badge_type)}
                      disabled={submitting}
                      variant="danger"
                      size="sm"
                    >
                      {submitting ? 'Revoking...' : 'Confirm revoke'}
                    </CustomButton>
                    <CustomButton
                      onClick={() => {
                        setRevokeReason('')
                        setRevokingBadgeType(null)
                      }}
                      variant="secondary"
                      size="sm"
                    >
                      Cancel
                    </CustomButton>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {canEdit && (
        <div className="border-t border-gray-200 dark:border-[#3d2c4f] pt-4">
          <h5 className="mb-3 font-medium text-gray-900 dark:text-gray-100">Award badge</h5>
          {!hasFoundingBadge && (
            <CustomButton
              onClick={handleAwardFounding}
              disabled={submitting}
              variant="primary"
              className="mr-2"
            >
              {submitting ? 'Awarding...' : 'Award Early Shops Program founding badge'}
            </CustomButton>
          )}
          {!showAwardForm ? (
            <CustomButton onClick={() => setShowAwardForm(true)} variant="secondary">
              Award custom badge
            </CustomButton>
          ) : (
            <div className="mt-3 space-y-2">
              <CustomTextInput
                label="Badge type"
                value={customBadgeType}
                onChange={(e) => setCustomBadgeType(e.target.value)}
                placeholder="e.g. echoes_partner"
              />
              <div className="flex gap-2">
                <CustomButton
                  onClick={handleAwardCustom}
                  disabled={submitting || !customBadgeType.trim()}
                  variant="primary"
                  size="sm"
                >
                  {submitting ? 'Awarding...' : 'Award'}
                </CustomButton>
                <CustomButton
                  onClick={() => {
                    setCustomBadgeType('')
                    setShowAwardForm(false)
                  }}
                  variant="secondary"
                  size="sm"
                >
                  Cancel
                </CustomButton>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
